import { useEffect, useReducer, useRef, useState } from "react";
import ContentGrid from "../../../components/layout/ContentGrid";
import { useEffectOnce } from "../../../components/hooks/useEffectOnce";
import ShipApiRepository from "../../../repositories/api/ShipApiRepository";
import { PilotageDetailsShipDialog } from "./PilotageDetailsShipDialog";
import { PhoneLink } from "./PilotageDetailsShipDialogContent";
import { BusySpinner } from "../../../components/layout/overlay/components/Busy";
import Icons from '../../../components/layout/icons/Icons';
import AppInsightsService from "../../../services/AppInsightsService";
import Spacer from "../../../components/layout/Spacer";
import { isNullOrEmpty, isNumeric } from "../../../components/helpers/ObjectHelpers";
import { Card } from "../../../components/layout/card/Card";
import '../styles/pilot.css';
import { ApplicationRightService } from 'services/ApplicationRightService';
import { ApplicationRightSystemNames } from 'components/constants/ApplicationRightSystemNames';
import { PilotageDetailsShipObservations } from "./PilotageDetailsShipObservations";
import FloatingButtonContainer from "components/layout/FloatingButtonContainer";
import FloatingActionButton from "components/layout/FloatingActionButton";
import { PlusIcon } from "components/layout/icons/Icons";
import { useApplicationRights } from "components/hooks/useAppStore";

export default function PilotageDetailsShip({ pilotage }) {
  const initialState = {
    shipContactInformation: {},
    hasImage: false,
    shipObservations: [],
    isFetchingObservations: true,
  };

  const Actions = {
    AddShipContactInformation: "AddShipContactInformation",
  };

  const pilotageShip = pilotage.pilotageShip;
  const imageUrl = `https://www.ship-info.com/vessels/${pilotageShip.imoNo}.jpg`;

  const [isModalMenuActive, setIsModalMenuActive] = useState(false);
  const [shipObservations, setShipObservations] = useState([]);
  const [shipObservationTypes, setShipObservationTypes] = useState([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [canViewSubDetails, setCanViewSubDetails] = useState(true);

    const isAllowedEdit = useRef(false);

    const observationsRef = useRef(null);

  const [pilotageDetails, dispatch] = useReducer(
    pilotageDetailsReducer,
    initialState,
    );

    const applicationRights = useApplicationRights();

  useEffectOnce(() => {
    fetchShipContactInformation();
  });

    useEffect(() => {
        if (!applicationRights) return;

        const applicationRightsService = new ApplicationRightService(applicationRights);
        const hasRight = applicationRightsService.canAccess([ApplicationRightSystemNames.WEB_MODULE_SHIP_OBSERVATION_WRITE]);
        isAllowedEdit.current = hasRight;

        setCanViewSubDetails(applicationRightsService.canAccess([
            ApplicationRightSystemNames.WEB_MODULE_MY_PILOT_ASSIGNMENTS_WRITE,
            ApplicationRightSystemNames.WEB_MODULE_CURRENT_PILOTAGES_READ]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationRights]);

  useEffect(() => {
    const loadImage = async () => {
      const img = new Image();
      img.src = imageUrl;

      await new Promise((resolve, reject) => {
        if (!isNumeric(pilotageShip.imoNo) || !pilotageShip.imoNo > 0) {
          setIsError(true);
          setIsImageLoaded(true)
        }

        img.onload = () => {
          setIsImageLoaded(true);
          resolve();
        };
        img.onerror = () => {
          setIsError(true);
          resolve();
        };
      }).catch((error) => {
          AppInsightsService.trackException(error);
          if (isNumeric(pilotageShip.imoNo) && pilotageShip.imoNo > 0) {
              console.error(error);
          }
      });

      setIsImageLoaded(true);
    };

    loadImage();
    fetchObservationsAsync();
    fetchObservationTypesAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  async function fetchShipContactInformation() {
    await ShipApiRepository.getShipDetailAsync(
      pilotageShip.shipId,
      pilotage.pilotageId,
    )
      .then((response) => {
        return response.json();
      })
      .then((result) => {
        handleAddShipContactInfo(result);
      })
      .catch((error) => {
        console.error(`An error occurred when fetching ship details.`, error);
      });
  }

  function handleAddShipContactInfo(shipContactInfo) {
    dispatch({
      type: Actions.AddShipContactInformation,
      shipContactInfo: shipContactInfo,
    });
  }

  function pilotageDetailsReducer(state, action) {
    switch (action.type) {
      case Actions.AddShipContactInformation: {
        return {
          ...state,
          shipContactInformation: action.shipContactInfo.shipContactInformation,
        };
      }
      default: {
        console.error(`Unknown action: ${action.type}`);
      }
    }
  }


    return (
      <>
        <ContentGrid
            title={pilotage.pilotageShip.shipName}
            subTitle={pilotage.pilotageNo}
            canCopySubTitle={true}
            titleIcon="ship">
            <div className="details-ship-image">
                {isError ? (
                    <Icons className="ob-dummy-image" iconName="dummy" dimensions={128} />
                ) : isImageLoaded ? (
                    <img className="ob-image" alt={pilotageShip.shipName} src={imageUrl} />
                ) : (
                    <div style={{ display: "flex", justifyContent: "space-around" }}>
                        <BusySpinner />
                    </div>
                )}
            </div>

            <Spacer/>
            <Card>
                <Spacer height={10} />
                <div className="font-size-lg k-font-bold">{pilotageShip.typeName}</div>
                <div className="pilotage-ship">
                    <div>
                        <span>
                            Kj.sign
                            <br />
                        </span>
                        {pilotageShip.callSign}
                    </div>
                    <div>
                        <span>
                            Land
                            <br />
                        </span>
                        {pilotageShip.country}
                    </div>

                    <div>
                        <span>
                            BT
                            <br />
                        </span>
                        {pilotageShip.grossTonnage}
                    </div>

                    <div>
                        <span>
                            Lengde
                            <br />
                        </span>
                        {pilotageShip.length}
                    </div>

                    <div>
                        <span>
                            Bredde
                            <br />
                        </span>
                        {pilotageShip.breadth}
                    </div>

                    <div>
                        <span>
                            Maks DPG
                            <br />
                        </span>
                        {pilotageShip.draught}
                    </div>

                    <div>
                        <span>
                            Aktuell DPG
                            <br />
                        </span>
                        {pilotage.actualDraft}
                    </div>

                    <div>
                        <span>
                            Airdraft
                            <br />
                        </span>
                        {pilotage.airDraft}
                    </div>
                    </div>
                </Card>
                {
                    (
                        !isNullOrEmpty(pilotageDetails.shipContactInformation.shipPhoneNo) ||
                        !isNullOrEmpty(pilotageDetails.shipContactInformation.shipMobilePhoneNo) ||
                        !isNullOrEmpty(pilotageDetails.shipContactInformation.shipAssistancePhoneNo)
                    ) &&
                    <>
                        <Spacer height={30} />
                        <Card title="Kontaktinformasjon">
                            <div className="pilotage-ship-contact-info">
                                <PhoneLink
                                    title={"Telefon"}
                                    phoneNumber={pilotageDetails.shipContactInformation.shipPhoneNo}
                                />
                                <PhoneLink
                                    title={"Mobil"}
                                    phoneNumber={
                                        pilotageDetails.shipContactInformation.shipMobilePhoneNo
                                    }
                                />
                                <PhoneLink
                                    title={"Vakttelefon"}
                                    phoneNumber={
                                        pilotageDetails.shipContactInformation.shipAssistancePhoneNo
                                    }
                                />
                            </div>
                        </Card>
                    </>
                }
            {
                (canViewSubDetails) &&
                <>
                        <Spacer height={20} />
                        <PilotageDetailsShipObservations
                            ref={observationsRef}
                            shipId={pilotage.shipId}
                            pilotageId={pilotage.pilotageId}
                            isAllowedEdit={isAllowedEdit.current}
                        />
                </>
            }

            <Spacer height={30} />
        </ContentGrid>

        {
            canViewSubDetails &&
            <FloatingButtonContainer hasBottomNavigation={true}>
                <FloatingActionButton
                    disabled={!isAllowedEdit}
                    themeColor={isAllowedEdit ? "primary" : "secondary"}
                    svgIcon={PlusIcon}
                    onClick={() => observationsRef.current.onAddObservation()}
                />
            </FloatingButtonContainer>
        }

        {
            <PilotageDetailsShipDialog
                isActive={isModalMenuActive}
                closeModalMenu={closeModalMenu}
                isAllowedEdit={isAllowedEdit.current}
                reloadObservations={fetchObservationsAsync}
                shipObservations={shipObservations}
                shipObservationTypes={shipObservationTypes}
                shipId={pilotageShip.shipId}
                pilotageId={pilotage.pilotageId}
            />
        }
    </>
  );

    async function fetchObservationsAsync() {
    const imageWidth = Math.floor(window.innerWidth * 1);
    const imageHeight = Math.floor(window.innerHeight * 1);

      const response = await ShipApiRepository.getShipObservationsAsync(
      pilotage.shipId,
      imageWidth,
      imageHeight,
    );
    if (response.ok === true) {
      const data = await response.json();

      setShipObservations(data);
    }
  }

  async function fetchObservationTypesAsync() {
    const response = await ShipApiRepository.getShipObservationTypesAsync();

    if (response.ok === true) {
      const data = await response.json();
      setShipObservationTypes(data);
    }
  }

  function closeModalMenu() {
    setIsModalMenuActive(false);
  }
}
